import { data_from_server_post } from '@/shared/http/client';

/**
 * Tri-state override: ``null`` means "Same as commercial" (inherit), ``""``
 * means "Disable" (explicitly nothing is sent to the SpecificAI model), and
 * any other trimmed text is a "Customize" override.
 */
export const normalizeStudentOverride = (raw) => {
    if (raw == null) return null;
    return raw.toString().trim();
};

/** Schema override shares the same tri-state semantics (``""`` = no schema). */
export const normalizeStudentSchemaOverride = normalizeStudentOverride;

/** Normalized snapshot of the three student override fields. */
export const normalizeStudentOverrides = (overrides) => ({
    systemPrompt: normalizeStudentOverride(overrides?.systemPrompt),
    userPrompt: normalizeStudentOverride(overrides?.userPrompt),
    schema: normalizeStudentSchemaOverride(overrides?.schema),
});

export const studentOverridesFromUsecase = (usecase) =>
    normalizeStudentOverrides({
        systemPrompt: usecase?.generative_student_instructions,
        userPrompt: usecase?.generative_student_prompt,
        schema: usecase?.generative_schema ?? null,
    });

export const studentOverridesEqual = (a, b) =>
    a.systemPrompt === b.systemPrompt &&
    a.userPrompt === b.userPrompt &&
    a.schema === b.schema;

/**
 * Persist the student model overrides (system prompt, user prompt, JSON
 * schema) via the `save_generative_settings` endpoint. A `null` field means
 * "Same as commercial" (inherit from the teacher setup), an empty string is a
 * deliberate "Disable" (nothing is sent — no prompt / no schema), and any
 * other text is a "Customize" override. The backend resolves inheritance at
 * training/evaluation time.
 *
 * No-op when nothing changed vs `comparedTo`. Resolves with the saved
 * usecase fields (for merging into local/context state) or `null` when
 * nothing was saved. Rejects on server failure.
 */
export async function saveStudentOverridesIfChanged({ usecase, overrides, comparedTo }) {
    if (!usecase?._id) return null;
    const next = normalizeStudentOverrides(overrides);
    const baseline =
        comparedTo !== undefined
            ? normalizeStudentOverrides(comparedTo)
            : studentOverridesFromUsecase(usecase);
    if (studentOverridesEqual(next, baseline)) return null;

    const savedFields = {
        generative_student_instructions: next.systemPrompt,
        generative_student_prompt: next.userPrompt,
        generative_schema: next.schema,
    };
    return new Promise((resolve, reject) => {
        data_from_server_post(
            'save_generative_settings',
            {
                client_id: usecase.client_id,
                llm_usecase_id: usecase._id,
                ...savedFields,
            },
            () => resolve(savedFields),
            (error) => reject(error instanceof Error ? error : new Error(String(error))),
        );
    });
}
